/**
 * Defines the types of tokens the lexer can produce.
 */
enum TokenType {
    WORD,
    QUOTED_STRING,
    AND,
    OR,
    NOT,
    NEAR,
    LPAREN,
    RPAREN,
    FIELD_PREFIX, // e.g., "author:"
    EOF, // End of File/Input
}

/**
 * Represents a single token with its type and value.
 */
interface Token {
    type: TokenType;
    value: string;
    distance?: number; // For NEAR/X operators
}

/**
 * Represents a node in the Abstract Syntax Tree (AST).
 * **FIXED**: Changed to a discriminated union for better type safety,
 * which resolves the TS4111 errors in Angular.
 */
interface BaseNode {
    type: 'AND' | 'OR' | 'NOT' | 'NEAR' | 'Term';
    field?: string;
}

interface TermNode extends BaseNode {
    type: 'Term';
    value: string;
}

interface NotNode extends BaseNode {
    type: 'NOT';
    operand: AstNode;
}

interface BinaryOpNode extends BaseNode {
    type: 'AND' | 'OR';
    left: AstNode;
    right: AstNode;
}

interface NearNode extends BaseNode {
    type: 'NEAR';
    left: AstNode;
    right: AstNode;
    distance: number;
}

export type AstNode = TermNode | NotNode | BinaryOpNode | NearNode;


/**
 * The Lexer (Tokenizer)
 * ---------------------
 * Scans the input string and converts it into a sequence of tokens.
 * This version intelligently groups consecutive words into a single phrase
 * and recognizes field prefixes (e.g., "author:").
 * @param input The boolean query string.
 * @param fieldNames A list of valid field names to recognize.
 * @returns An array of tokens.
 */
const tokenize = (input: string, fieldNames: string[] = []): Token[] => {
    const tokens: Token[] = [];
    let cursor = 0;

    const regex = {
        whitespace: /^\s+/,
        and: /^\bAND\b/i,
        or: /^\bOR\b/i,
        not: /^\bNOT\b/i,
        near: /^\bNEAR\/(\d+)\b/i,
        lparen: /^\(/,
        rparen: /^\)/,
        quoted: /^"([^"]*)"/,
        word: /^[a-zA-Z0-9_]+\*?/,
        fieldPrefix: /^([a-zA-Z0-9_]+):/,
        operatorOrParen: /^\b(AND|OR|NOT|NEAR\/\d+)\b|^\(|^\)/i
    };

    while (cursor < input.length) {
        let remaining = input.substring(cursor);
        let match;

        // Check for field prefixes first.
        const fieldMatch = remaining.match(regex.fieldPrefix);
        if (fieldMatch && fieldNames.includes(fieldMatch[1])) {
            tokens.push({ type: TokenType.FIELD_PREFIX, value: fieldMatch[1] });
            cursor += fieldMatch[0].length; // Consume 'word:'
            continue;
        }

        if (match = remaining.match(regex.whitespace)) {
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.and)) {
            tokens.push({ type: TokenType.AND, value: 'AND' });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.or)) {
            tokens.push({ type: TokenType.OR, value: 'OR' });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.not)) {
            tokens.push({ type: TokenType.NOT, value: 'NOT' });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.near)) {
            const distance = parseInt(match[1], 10);
            tokens.push({ type: TokenType.NEAR, value: `NEAR/${distance}`, distance });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.lparen)) {
            tokens.push({ type: TokenType.LPAREN, value: '(' });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.rparen)) {
            tokens.push({ type: TokenType.RPAREN, value: ')' });
            cursor += match[0].length;
            continue;
        }
        if (match = remaining.match(regex.quoted)) {
            tokens.push({ type: TokenType.QUOTED_STRING, value: match[1] });
            cursor += match[0].length;
            continue;
        }

        // Logic for implicit phrases (e.g., "big bird")
        if (match = remaining.match(regex.word)) {
            let phrase = [match[0]];
            let phraseEndCursor = cursor + match[0].length;

            while (true) {
                let afterPhrase = input.substring(phraseEndCursor);
                let spaceMatch = afterPhrase.match(regex.whitespace);
                if (!spaceMatch) break;

                let potentialNextWordPos = phraseEndCursor + spaceMatch[0].length;
                let afterSpace = input.substring(potentialNextWordPos);

                const nextIsField = afterSpace.match(regex.fieldPrefix);
                if (nextIsField && fieldNames.includes(nextIsField[1])) {
                    break;
                }

                if (afterSpace === '' || afterSpace.match(regex.operatorOrParen)) {
                    break;
                }

                let nextWordMatch = afterSpace.match(regex.word);
                if (nextWordMatch) {
                    phrase.push(nextWordMatch[0]);
                    phraseEndCursor = potentialNextWordPos + nextWordMatch[0].length;
                } else {
                    break;
                }
            }

            if (phrase.length > 1) {
                tokens.push({ type: TokenType.QUOTED_STRING, value: phrase.join(' ') });
            } else {
                tokens.push({ type: TokenType.WORD, value: phrase[0] });
            }
            cursor = phraseEndCursor;
            continue;
        }

        throw new Error(`Unexpected character at position ${cursor}: ${input[cursor]}`);
    }

    tokens.push({ type: TokenType.EOF, value: '' });
    return tokens;
};


/**
 * The Parser
 * ----------
 * Takes a stream of tokens and builds an Abstract Syntax Tree (AST).
 */
class Parser {
    private tokens: Token[] = [];
    private position: number = 0;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
    }

    private currentToken(): Token {
        return this.tokens[this.position];
    }

    private consume(type: TokenType): Token {
        const token = this.currentToken();
        if (token.type === type) {
            this.position++;
            return token;
        }
        throw new Error(`Expected token ${TokenType[type]} but found ${TokenType[token.type]}`);
    }

    // Grammar rule: atom -> (FIELD_PREFIX atom) | WORD | QUOTED_STRING | LPAREN expression RPAREN
    private parseAtom(): AstNode {
        const token = this.currentToken();

        if (token.type === TokenType.FIELD_PREFIX) {
            this.consume(TokenType.FIELD_PREFIX);
            const subExpression = this.parseAtom(); // Recurse to get the value

            // Helper to apply a field to all terms in a sub-expression
            const applyField = (node: AstNode, field: string): AstNode => {
                // Because AstNode is a discriminated union, TypeScript now understands
                // the shape of 'node' inside each 'if' block, allowing safe dot notation.
                if (node.type === 'Term') {
                    // Innermost field specifier wins, e.g., field1:(field2:value)
                    return { ...node, field: node.field || field };
                }
                if (node.type === 'AND' || node.type === 'OR') {
                    return { ...node, left: applyField(node.left, field), right: applyField(node.right, field) };
                }
                if (node.type === 'NOT') {
                    return { ...node, operand: applyField(node.operand, field) };
                }
                return node;
            };

            return applyField(subExpression, token.value);
        }

        if (token.type === TokenType.WORD || token.type === TokenType.QUOTED_STRING) {
            this.consume(token.type);
            return { type: 'Term', value: token.value };
        }

        if (token.type === TokenType.LPAREN) {
            this.consume(TokenType.LPAREN);
            const expr = this.parseExpression();
            this.consume(TokenType.RPAREN);
            return expr;
        }

        throw new Error(`Unexpected token in atom: ${TokenType[token.type]}`);
    }

    // Grammar rule: factor -> (NOT)? atom
    private parseFactor(): AstNode {
        if (this.currentToken().type === TokenType.NOT) {
            this.consume(TokenType.NOT);
            return { type: 'NOT', operand: this.parseFactor() };
        }
        return this.parseAtom();
    }

    // Grammar rule: term -> factor ((AND | NEAR/X) factor)*
    private parseTerm(): AstNode {
        let node = this.parseFactor();
        while (this.currentToken().type === TokenType.AND || this.currentToken().type === TokenType.NEAR) {
            const operatorToken = this.currentToken();
            if (operatorToken.type === TokenType.AND) {
                this.consume(TokenType.AND);
                node = { type: 'AND', left: node, right: this.parseFactor() };
            } else if (operatorToken.type === TokenType.NEAR) {
                this.consume(TokenType.NEAR);
                const distance = operatorToken.distance || 5; // Default distance if not specified
                node = { type: 'NEAR', left: node, right: this.parseFactor(), distance };
            }
        }
        return node;
    }

    // Grammar rule: expression -> term (OR term)*
    private parseExpression(): AstNode {
        let node = this.parseTerm();
        while (this.currentToken().type === TokenType.OR) {
            this.consume(TokenType.OR);
            node = { type: 'OR', left: node, right: this.parseTerm() };
        }
        return node;
    }

    public parse(): AstNode {
        const ast = this.parseExpression();
        if (this.currentToken().type !== TokenType.EOF) {
            throw new Error("Unexpected tokens at the end of query.");
        }
        return ast;
    }
}

/**
 * Main parse function to be exported or used.
 * @param input The boolean query string.
 * @param fieldNames An optional array of valid field names.
 * @returns The structured AST.
 */
export const parseBooleanQuery = (input: string, fieldNames: string[] = []): AstNode | { error: string } => {
    if (!input.trim()) {
        // Return a valid TermNode for empty input
        return { type: 'Term', value: '' };
    }
    try {
        const tokens = tokenize(input, fieldNames);
        const parser = new Parser(tokens);
        return parser.parse();
    } catch (e: any) {
        return { error: e.message };
    }
};
